import { Link } from 'react-router-dom';
import { ArrowRight, Quote, Phone, Mail, Linkedin, Award, Globe, Users, Wheat } from 'lucide-react';

// Photos
const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/yovkyt7h_WhatsApp%20Image%202026-03-15%20at%203.53.53%20PM.jpeg";
const PARTNER_PHOTO = "https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/r5utjt4t_WhatsApp%20Image%202026-03-15%20at%203.55.58%20PM.jpeg";

const Leadership = () => {
  const highlights = [
    { icon: Award, value: '30+', label: 'Years in Rice Industry' },
    { icon: Globe, value: '4', label: 'Continents Served' },
    { icon: Users, value: '1000+', label: 'Farming Relationships' },
    { icon: Wheat, value: 'Expert', label: 'All Basmati Varieties' }
  ];

  return (
    <main data-testid="leadership-page">
      {/* Hero Section */}
      <section
        data-testid="leadership-hero"
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1624547481160-d564f43324f9?w=1920')`
          }}
        />
        <div className="hero-gradient absolute inset-0" />

        {/* Embossed crest watermarks */}
        <div
          className="crest-watermark crest-watermark-center"
          style={{ backgroundImage: "url('/sawariya-logo.png')" }}
          aria-hidden="true"
        />
        <div
          className="crest-watermark crest-watermark-corner"
          style={{ backgroundImage: "url('/sawariya-logo.png')" }}
          aria-hidden="true"
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-24 pb-12 text-center">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block animate-fade-up">
            Our Leadership
          </span>
          <h1
            data-testid="leadership-title"
            className="font-heading text-4xl md:text-6xl font-bold text-white mb-4 animate-fade-up delay-100"
          >
            The People Behind <span className="text-gold">Sawariya</span>
          </h1>
          <p className="font-body text-lg text-white/70 max-w-2xl mx-auto animate-fade-up delay-200">
            Decades of expertise, generations of trust
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section data-testid="founder-section" className="py-20 md:py-32 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-start">
            {/* Founder Photo */}
            <div className="relative">
              <div className="relative">
                <img
                  src={FOUNDER_PHOTO}
                  alt="Mr. Rajneesh Goel - Founder"
                  className="w-full max-w-lg mx-auto shadow-2xl"
                />
                {/* Name Badge */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-navy text-white px-8 py-4 shadow-xl text-center min-w-[280px]">
                  <h3 className="font-heading text-2xl font-bold text-gold">Rajneesh Goel</h3>
                  <p className="font-body text-sm tracking-widest uppercase mt-1">Founder</p>
                </div>
              </div>
              
              {/* Experience Badge */}
              <div className="absolute -top-4 -right-4 md:top-4 md:right-0 bg-gold text-navy-dark p-4 shadow-lg">
                <div className="font-heading text-3xl font-bold">30+</div>
                <div className="font-body text-xs tracking-widest uppercase">Years</div>
              </div>
            </div>

            {/* Founder Bio */}
            <div className="pt-8 lg:pt-0">
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                Founder & Visionary
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                Building Excellence,<br />
                <span className="text-gold">One Grain</span> at a Time
              </h2>
              <div className="w-16 h-0.5 bg-gold mb-8" />
              
              <div className="space-y-6 font-body text-lg text-gray-700 leading-relaxed">
                <p>
                  <strong className="text-navy">Mr. Rajneesh Goel</strong> founded Sawariya Global Exports LLP from the ground up — building every relationship, every standard, and every system personally over more than <strong className="text-navy">30 years in the rice industry</strong>.
                </p>
                <p>
                  His expertise is not theoretical. Long before Sawariya became a name trusted by importers across four continents, Mr. Goel was walking paddy fields in <strong className="text-navy">Punjab, Haryana, and Uttarakhand</strong> — learning the crop directly from the farmers who grow it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section data-testid="founder-quote" className="py-16 md:py-24 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        
        <div className="container mx-auto px-4 md:px-8 max-w-4xl relative z-10 text-center">
          <Quote className="w-16 h-16 text-gold/30 mx-auto mb-6" />
          <blockquote className="font-accent text-xl md:text-2xl lg:text-3xl text-white leading-relaxed italic mb-8">
            "Today, he remains known by name in the farming communities that supply India's finest basmati — a trust earned through decades of honest dealing, deep agronomic knowledge, and a genuine understanding of every variety."
          </blockquote>
          <div className="w-24 h-0.5 bg-gold mx-auto" />
        </div>
      </section>

      {/* Expertise Section */}
      <section data-testid="expertise-section" className="py-20 md:py-32 diagonal-pattern relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
                Unmatched Expertise
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                The Living Quality<br />
                <span className="text-gold">Standard</span>
              </h2>
              <div className="w-16 h-0.5 bg-gold mb-8" />
              
              <div className="space-y-6 font-body text-lg text-gray-700 leading-relaxed">
                <p>
                  His mastery of rice — <strong className="text-navy">how it grows, how it mills, how it ages, and how it performs</strong> in kitchens from London to Riyadh — is the living quality standard behind every consignment Sawariya ships.
                </p>
                <p>
                  From <strong className="text-gold">1121 Extra Long Grain</strong> to <strong className="text-gold">Traditional Basmati</strong>, Mr. Goel's hands-on knowledge ensures that every grain meets the exacting standards that have made Sawariya a trusted name worldwide.
                </p>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-2 gap-4 mt-10">
                {highlights.map((item, index) => (
                  <div key={index} className="bg-white p-4 border-l-4 border-gold shadow-sm">
                    <item.icon className="w-6 h-6 text-navy mb-2" />
                    <div className="font-heading text-2xl font-bold text-navy">{item.value}</div>
                    <div className="font-body text-xs tracking-wider uppercase text-gray-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Image Collage */}
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400"
                  alt="Premium rice grains"
                  className="w-full h-40 object-cover shadow-lg"
                />
                <div className="bg-navy p-6 text-center">
                  <div className="font-heading text-3xl font-bold text-gold">1989</div>
                  <div className="font-body text-xs text-white/70 tracking-widest uppercase">Founded</div>
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="bg-gold p-6 text-center">
                  <div className="font-heading text-3xl font-bold text-navy">Trust</div>
                  <div className="font-body text-xs text-navy/70 tracking-widest uppercase">Built Over Decades</div>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1648102796336-0a5f2c417770?w=400"
                  alt="Rice paddy fields"
                  className="w-full h-40 object-cover shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Section */}
      <section data-testid="partner-section" className="py-20 md:py-32 leaf-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center mb-12">
            <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
              Next Generation
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
              Continuing the <span className="text-gold">Legacy</span>
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto" />
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-white shadow-xl overflow-hidden">
              {/* Partner Photo */}
              <div className="relative h-full">
                <img
                  src={PARTNER_PHOTO}
                  alt="Rudraksh Goel - Partner"
                  className="w-full h-full min-h-[400px] object-cover object-top"
                />
              </div>
              
              {/* Partner Info */}
              <div className="p-8 md:p-12">
                <div className="border-l-4 border-gold pl-6 mb-6">
                  <h3 className="font-heading text-3xl font-bold text-navy">Rudraksh Goel</h3>
                  <p className="font-body text-gold tracking-widest uppercase text-sm mt-1">Partner</p>
                </div>
                
                <p className="font-body text-gray-600 leading-relaxed mb-8">
                  Carrying forward the family legacy with a focus on international expansion, quality innovation, and building lasting partnerships across global markets. Bringing fresh perspectives while honoring the traditions that made Sawariya a trusted name.
                </p>
                
                <div className="space-y-4">
                  <a
                    href="tel:+919896557781"
                    className="flex items-center gap-3 text-navy hover:text-gold transition-colors group"
                  >
                    <div className="w-10 h-10 bg-cream rounded-full flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                      <Phone size={18} />
                    </div>
                    <span className="font-body">+91-9896557781</span>
                  </a>
                  <a
                    href="mailto:rudraksh@sawariyaglobalexports.com"
                    className="flex items-center gap-3 text-navy hover:text-gold transition-colors group"
                  >
                    <div className="w-10 h-10 bg-cream rounded-full flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                      <Mail size={18} />
                    </div>
                    <span className="font-body text-sm">rudraksh@sawariyaglobalexports.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="leadership-cta" className="py-16 bg-navy">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-2">
                Ready to Partner With Us?
              </h3>
              <p className="font-body text-white/70">
                Experience the Sawariya difference — where expertise meets excellence
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="leadership-contact-btn"
              className="inline-flex items-center gap-3 bg-gold text-navy-dark px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-gold-light transition-colors"
            >
              Contact Us
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Leadership;
